import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TextField as MUITextField,
  Stack,
} from '@mui/material'
import { QcTaskEsignContext } from '@common/config/api/client'
import { styled } from '@mui/material/styles'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth.ts'
import { useSignPayload } from '../../hooks/useSignPayload'
import { useParams } from 'react-router-dom'
import { SPACING } from '@common/theme/spacing'
import { FormEvent } from 'react'
import { requestEsignReauthentication } from '@auth/utils/requestEsignReauthentication'

type ESignDialogProps = {
  onClose: () => void
  payload: QcTaskEsignContext
}

const ESignDialogStyled = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    padding: theme.spacing(SPACING.spacingXl),
    borderRadius: theme.spacing(SPACING.borderRadiusXl),
  },
  '& .MuiDialogActions-root': {
    paddingTop: theme.spacing(SPACING.spacingXl),
  },
}))

export default function ESignDialog({ onClose, payload }: ESignDialogProps) {
  const { user } = useExtendedAuth()
  const { isPending } = useSignPayload()
  const { studyId, subjectId, timepointSubmissionId } = useParams<{
    studyId: string
    subjectId: string
    timepointSubmissionId: string
  }>()

  const getSignerName = () => {
    const firstName = user?.profile.given_name || ''
    const lastName = user?.profile.family_name || ''
    let signerName = `${firstName} ${lastName}`.trim()

    if (!signerName) {
      signerName = user?.profile.email || user?.profile.preferred_username || ''
    }

    return signerName
  }

  const requestReAuthentication = async () => {
    const signerName = getSignerName()
    // Modal purpose is complete once Sign is chosen; re-auth continues in the popup.
    onClose()

    try {
      await requestEsignReauthentication(payload, {
        pathParams: {
          study_id: studyId!,
          subject_id: subjectId!,
          timepoint_submission_id: timepointSubmissionId!,
        },
        signer_name: signerName,
      })
    } catch {
      // Toast/cleanup handled by requestEsignReauthentication
    }
  }

  const handleSignClick = async (event: FormEvent) => {
    event.preventDefault()
    await requestReAuthentication()
  }

  return (
    <ESignDialogStyled sx={{ p: SPACING.spacingXl }} open={true} onClose={onClose}>
      <form onSubmit={handleSignClick}>
        <DialogTitle sx={{ p: SPACING.noSpacing }}>
          <Typography variant="h6">Sign to confirm</Typography>
          <Typography variant="body1" color="text.secondary" width={530}>
            Authentication is required to complete this action
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: SPACING.noSpacing, pb: SPACING.noSpacing }}>
          <Stack gap={SPACING.spacingLg} pt={SPACING.spacingXl} mb={SPACING.spacingXl}>
            <MUITextField
              label="Your Name"
              variant="outlined"
              fullWidth
              disabled
              value={getSignerName()}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
            <MUITextField
              label="Email"
              variant="outlined"
              fullWidth
              disabled
              value={user?.profile.preferred_username}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            For security purposes, you will need to re-authenticate to confirm this action.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            **Regulatory: The electronic signatures in this system are intended to be the legally binding equivalent of
            the individual's handwritten signature.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: SPACING.noSpacing }}>
          <Button size="large" variant="contained" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="large" variant="contained" type="submit" disabled={isPending}>
            Sign
          </Button>
        </DialogActions>
      </form>
    </ESignDialogStyled>
  )
}
